/**
 * Portal Course Group View API Client
 *
 * Handles read-only operations for teachers and parents viewing course groups.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to course group view endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/course-group-view`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

// ==================== Course Group View API ====================

/**
 * List course groups the current user is associated with.
 * - Teachers: Course groups they are assigned to
 * - Parents: Course groups their children are enrolled in
 */
export const listMyCourseGroups = async (viewAs = null) => {
  const data = {};
  if (viewAs) data.viewAs = viewAs;
  const result = await apiRequest('listMyCourseGroups', data);
  return {
    courseGroups: result.courseGroups || [],
    role: result.role
  };
};

/**
 * Get full details of a course group including teachers, counts.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const getCourseGroupDetail = async (courseGroupId, viewAs = null) => {
  const data = { courseGroupId };
  if (viewAs) data.viewAs = viewAs;
  const result = await apiRequest('getCourseGroupDetail', data);
  return {
    courseGroup: result.courseGroup,
    teachers: result.teachers || [],
    studentCount: result.studentCount || 0,
    materialCount: result.materialCount || 0,
    answerCount: result.answerCount || 0,
    feedbackCount: result.feedbackCount || 0,
    pendingFeedbackCount: result.pendingFeedbackCount || 0,
    role: result.role
  };
};

/**
 * Get student list for a course group.
 * Teachers and admins only.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const getStudentList = async (courseGroupId) => {
  const result = await apiRequest('getStudentList', { courseGroupId });
  return result.students || [];
};

/**
 * Get statistics for the current teacher.
 * Teachers only.
 *
 * @returns {Object} { classesCount, studentsCount }
 */
export const getTeacherStats = async () => {
  const result = await apiRequest('getTeacherStats', {});
  return {
    classesCount: result.classesCount || 0,
    studentsCount: result.studentsCount || 0
  };
};

// ==================== Utility Functions ====================

/**
 * Format course group name for display.
 * Includes type prefix if available (e.g., "1to1 | Math Y1T2")
 */
export const formatCourseGroupName = (courseGroup) => {
  if (!courseGroup) return '';
  let yearLabel;
  if (courseGroup.year === -1) {
    yearLabel = 'Kindy';
  } else if (courseGroup.year === 0) {
    yearLabel = 'PP';
  } else {
    yearLabel = `Y${courseGroup.year}`;
  }
  const baseName = `${courseGroup.title || ''} ${yearLabel}T${courseGroup.term || ''}`;

  // Add type prefix if available
  const typePrefix = courseGroup.typeDisplayName
    ? `${courseGroup.typeDisplayName} | `
    : '';

  return `${typePrefix}${baseName}`;
};

/**
 * Format week display.
 */
export const formatWeek = (week, isHoliday) => {
  if (isHoliday) return 'Holiday';
  if (week <= 0) return 'Not Started';
  return `Week ${week}`;
};

/**
 * Check if material is accessible based on week.
 * For teachers: current week +/- 1
 */
export const isTeacherMaterialAccessible = (materialWeek, currentWeek) => {
  return materialWeek >= currentWeek - 1 && materialWeek <= currentWeek + 1;
};

/**
 * Get role display info.
 */
export const getRoleInfo = (role) => {
  switch (role) {
    case 'teacher':
      return { label: 'Teacher', color: 'primary', icon: 'bi-person-workspace' };
    case 'parent':
      return { label: 'Parent', color: 'success', icon: 'bi-person-hearts' };
    case 'admin':
      return { label: 'Admin', color: 'danger', icon: 'bi-shield-check' };
    default:
      return { label: role, color: 'secondary', icon: 'bi-person' };
  }
};

const courseGroupViewApi = {
  listMyCourseGroups,
  getCourseGroupDetail,
  getStudentList,
  getTeacherStats,
  formatCourseGroupName,
  formatWeek,
  isTeacherMaterialAccessible,
  getRoleInfo
};

export default courseGroupViewApi;
